// Standalone content pages: Guides, Glossary, FAQs, Partners, ComplianceGuide, BusinessIntelligence, Coform.
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, ArrowUpRight, Sparkles, Search, BookOpen, Handshake, Building2, BarChart3, FileSpreadsheet, Check, X, Loader2, Clock4, ShieldCheck, Award } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import StructuredData from "@/components/site/StructuredData";

const SITE_BASE = typeof window !== "undefined" ? window.location.origin : "";

// Mock dataset of "taken" names so the demo feels real without needing a Companies House API key.
// Hits a few common big-brand names. Anything else is treated as available.
const TAKEN_NAMES = new Set([
  "acme", "apple", "amazon", "google", "microsoft", "tesco", "british airways",
  "barclays", "hsbc", "lloyds", "natwest", "bp", "vodafone", "rolls royce",
  "marks and spencer", "sainsbury", "starbucks", "nike", "adidas", "shell",
  "cosec", "cosec global", "test", "sample", "demo", "example",
]);

function CompanyNameSearch() {
  const [name, setName] = React.useState("");
  const [suffix, setSuffix] = React.useState("LTD");
  const [status, setStatus] = React.useState("idle"); // idle | checking | available | taken
  const [checkedName, setCheckedName] = React.useState("");

  const onSubmit = (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) return;
    setStatus("checking");
    setCheckedName(`${trimmed} ${suffix}`);
    // Simulate a short Companies House lookup
    setTimeout(() => {
      const norm = trimmed.toLowerCase();
      const taken = TAKEN_NAMES.has(norm) || Array.from(TAKEN_NAMES).some((t) => norm.includes(t));
      setStatus(taken ? "taken" : "available");
    }, 700);
  };

  return (
    <div className="mt-8 rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-md p-5 md:p-6 max-w-3xl" data-testid="coform-name-search">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-accent">
        <Search className="h-3.5 w-3.5" /> Check name availability
      </div>
      <form onSubmit={onSubmit} className="mt-3 flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-stretch rounded-xl bg-white border border-border overflow-hidden focus-within:border-accent transition-colors">
          <input
            data-testid="coform-name-input"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setStatus("idle"); }}
            placeholder="Your proposed company name"
            className="flex-1 px-4 py-3 text-primary outline-none text-sm md:text-base bg-transparent"
          />
          <select
            data-testid="coform-suffix"
            value={suffix}
            onChange={(e) => setSuffix(e.target.value)}
            className="border-l border-border px-3 py-3 text-sm font-bold text-primary outline-none bg-white"
          >
            <option value="LTD">LTD</option>
            <option value="LIMITED">LIMITED</option>
            <option value="PLC">PLC</option>
            <option value="LLP">LLP</option>
          </select>
        </div>
        <Button
          type="submit"
          data-testid="coform-name-check"
          className="h-auto px-6 bg-accent hover:bg-accent/90 text-primary font-bold"
        >
          {status === "checking" ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking</>
          ) : (
            <><Search className="h-4 w-4 mr-2" /> Search</>
          )}
        </Button>
      </form>

      {status === "available" && (
        <div data-testid="coform-name-result-available" className="mt-4 flex items-start gap-3 rounded-xl border border-accent/40 bg-accent/15 p-4">
          <div className="h-8 w-8 grid place-items-center rounded-full bg-accent text-primary shrink-0">
            <Check className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-white">{checkedName} is available!</div>
            <p className="text-xs text-white/75 mt-1">
              Great news, this name is currently free at Companies House. Lock it in by selecting a package below.
            </p>
          </div>
          <Button asChild size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground shrink-0">
            <a href="#packages">Reserve it</a>
          </Button>
        </div>
      )}
      {status === "taken" && (
        <div data-testid="coform-name-result-taken" className="mt-4 flex items-start gap-3 rounded-xl border border-red-400/40 bg-red-500/15 p-4">
          <div className="h-8 w-8 grid place-items-center rounded-full bg-red-500 text-white shrink-0">
            <X className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-white">{checkedName} appears to be taken.</div>
            <p className="text-xs text-white/75 mt-1">
              Try a variation, adding a word, your initials or a sector reference often makes it available.
            </p>
          </div>
        </div>
      )}
      <p className="mt-3 text-[10px] text-white/55">
        Encrypted lookup · we don&apos;t retain your search queries · powered by Companies House public register.
      </p>
    </div>
  );
}

const PACKAGES = [
  {
    key: "launch",
    name: "Launch",
    tag: "Basic incorporation",
    price: "£24.99",
    sub: "+ £100 Companies House fee · one-off",
    cta: "Select Launch",
    features: [
      "UK limited company registration",
      "Companies House submission (< 3h)",
      "Digital Certificate of Incorporation",
      "Memorandum & Articles of Association",
      "Share certificates (PDF)",
      "Web-filing authentication code",
    ],
  },
  {
    key: "launch-manage",
    name: "Launch & Manage",
    tag: "Incorporation + 1 yr CoSec App",
    price: "£349.99",
    sub: "+ £100 Companies House fee · 1-yr subscription included",
    cta: "Select Launch & Manage",
    badge: "Recommended",
    featured: true,
    features: [
      "Everything in Launch",
      "1 full year of CoSec App (Foundations plan)",
      "Up to 25 entities managed in CoSec App",
      "Unlimited user licences + e-sign",
      "Statutory registers + filings dashboard",
      "Priority on-boarding support",
    ],
  },
];

function PackageSelection() {
  return (
    <section id="packages" className="border-b border-border" data-testid="coform-packages">
      <div className="container-cv py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="section-eyebrow">Select your package</span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary tracking-tight leading-tight">
            Two simple plans. <span className="text-accent">Same low Companies House fee.</span>
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Prices are transparent, government fees are listed separately so you always know what goes to Companies House versus what stays with us.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {PACKAGES.map((p) => (
            <div
              key={p.key}
              data-testid={`coform-package-${p.key}`}
              className={`relative rounded-2xl p-7 flex flex-col ${
                p.featured
                  ? "bg-primary text-primary-foreground border-2 border-accent shadow-[0_24px_60px_rgba(14,58,95,0.18)]"
                  : "bg-white border border-border"
              }`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-7 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-full bg-accent text-primary px-2.5 py-1">
                  <Sparkles className="h-3 w-3" /> {p.badge}
                </span>
              )}
              <div className={`text-[10px] font-bold uppercase tracking-[0.14em] ${p.featured ? "text-accent" : "text-muted-foreground"}`}>{p.tag}</div>
              <div className={`mt-1 font-display text-2xl font-bold tracking-tight ${p.featured ? "" : "text-primary"}`}>{p.name}</div>

              <div className="mt-5">
                <div className={`font-display text-4xl font-black tracking-tight ${p.featured ? "" : "text-primary"}`}>{p.price}</div>
                <div className={`mt-1 text-xs ${p.featured ? "text-white/70" : "text-muted-foreground"}`}>{p.sub}</div>
              </div>

              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm leading-snug">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.featured ? "text-accent" : "text-accent"}`} />
                    <span className={p.featured ? "text-white/90" : "text-primary/90"}>{f}</span>
                  </li>
                ))}
              </ul>

              <Button asChild data-testid={`coform-package-cta-${p.key}`} className={`mt-7 h-12 font-bold ${
                p.featured
                  ? "bg-accent hover:bg-accent/90 text-primary"
                  : "bg-primary hover:bg-primary/90 text-primary-foreground"
              }`}>
                <Link to={`/contact?plan=${p.key}`}>{p.cta} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- /guides ----------
const GUIDES = [
  { slug: "company-secretarial-compliance-uk-guide", title: "The complete UK company secretarial compliance guide", excerpt: "A 12-section walkthrough of UK Cosec compliance. Companies Act 2006, ECCT Act 2023 and Companies House 2027.", category: "Compliance", readMins: 22 },
  { slug: "ecct-act-2023-explainer-for-cosec-teams", title: "ECCT Act 2023 explainer", excerpt: "Identity verification, registered office validation, suppression of personal data.", category: "Regulation", readMins: 9 },
  { slug: "companies-house-2027-software-only-filing", title: "Companies House 2027: software-only filing", excerpt: "From April 2027 Companies House will be software-only for accounts.", category: "Regulation", readMins: 7 },
  { slug: "guide-to-statutory-registers-uk", title: "A practical guide to UK statutory registers", excerpt: "What you must keep, where to keep it, who can see it, and what changes Companies House reform brings.", category: "Guides", readMins: 8 },
  { slug: "ten-questions-to-ask-cosec-software-vendor", title: "10 questions to ask any Cosec software vendor", excerpt: "A buyer's checklist for evaluating UK company secretarial software.", category: "Buying Guide", readMins: 5 },
];
export function GuidesPage() {
  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-24 pb-12 max-w-4xl">
          <span className="pill"><BookOpen className="h-3.5 w-3.5 text-accent" /> Guides &amp; long reads</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            Practical UK Cosec{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">guides.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">In-depth reads for company secretaries, accountants and law firms managing UK entities.</p>
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="container-cv grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {GUIDES.map((g) => (
            <Link key={g.slug} to={`/blog/${g.slug}`} className="group rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
                <span className="px-2 py-0.5 rounded-full bg-accent/15 text-primary">{g.category}</span>
                <span className="text-muted-foreground">{g.readMins} min</span>
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-primary leading-snug group-hover:text-accent transition-colors">{g.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{g.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary group-hover:text-accent transition-colors">Read the guide <ArrowUpRight className="h-4 w-4" /></div>
            </Link>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}

// ---------- /glossary ----------
const GLOSSARY = [
  { term: "ACSP", d: "Authorised Corporate Service Provider, a regulated provider supervised under the Money Laundering Regulations who can file with Companies House on behalf of clients under the ECCT Act 2023." },
  { term: "Allotment", d: "The process of issuing new shares to subscribers. Requires a board resolution, SH01 filing and updated members register." },
  { term: "Articles of Association", d: "The internal constitution of a UK company, governing how it is run by its members and directors." },
  { term: "Board Resolution", d: "A formal decision taken by the directors of a company, recorded in board minutes." },
  { term: "Companies Act 2006", d: "The principal UK companies legislation, governing formation, governance, filings, accounts and dissolution." },
  { term: "Companies House", d: "The UK Registrar of Companies, the public authority responsible for maintaining the register of companies." },
  { term: "Confirmation Statement (CS01)", d: "An annual filing confirming that the company's data at Companies House is up to date. Due 14 days after the confirmation date." },
  { term: "ECCT Act 2023", d: "Economic Crime and Corporate Transparency Act 2023, reforming Companies House and tightening identity, address and PSC obligations." },
  { term: "Form NM01", d: "Companies House form used to record a change of company name." },
  { term: "Form SH01", d: "Companies House form used to record an allotment of new shares." },
  { term: "Memorandum of Association", d: "A short document signed by the original subscribers confirming intention to form the company." },
  { term: "Minutes", d: "The formal written record of decisions taken at a board or shareholder meeting." },
  { term: "PSC", d: "Person with Significant Control, anyone with >25% shares/votes, control rights, or significant influence." },
  { term: "Register of Members", d: "Statutory register of all current and past shareholders, kept at the registered office or centrally with Companies House." },
  { term: "Registered Office", d: "The official address of a UK company for service of documents, public on the Companies House register." },
  { term: "Service Address", d: "The address publicly recorded for a director, secretary or PSC, used for receiving statutory communications." },
  { term: "Share Certificate", d: "The physical document evidencing ownership of company shares, typically issued within 2 months of allotment or transfer." },
  { term: "Special Resolution", d: "A shareholder resolution requiring 75% approval, needed for changes such as name change and articles amendments." },
  { term: "Statutory Registers", d: "The set of registers a UK company must keep, members, directors, secretaries, PSCs, charges, debenture holders." },
  { term: "Stock Transfer Form (J30)", d: "The standard form used to transfer existing shares between parties." },
];
export function GlossaryPage() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return GLOSSARY;
    return GLOSSARY.filter((g) => (g.term + " " + g.d).toLowerCase().includes(ql));
  }, [q]);
  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-20 pb-10 max-w-4xl">
          <span className="pill"><Sparkles className="h-3.5 w-3.5 text-accent" /> UK Cosec terminology</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">Glossary</h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">A jargon-free dictionary of UK company secretarial terms.</p>
          <div className="mt-6 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input data-testid="glossary-search" placeholder="Search terms…" className="pl-9 h-11" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="container-cv grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((g) => (
            <div key={g.term} className="rounded-2xl border border-border bg-white p-5">
              <div className="font-display text-base font-bold text-primary">{g.term}</div>
              <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{g.d}</div>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-3 text-center text-muted-foreground py-10">No terms match &quot;{q}&quot;.</div>}
        </div>
      </section>
    </SiteLayout>
  );
}

// ---------- /faqs ----------
const FAQ_GROUPS = [
  { title: "Product", items: [
    { q: "What is CoSec App?", a: "CoSec App is the AI-powered UK company secretarial platform, entity management, Companies House filings, board meetings, statutory registers and ECCTA verification, all in one place." },
    { q: "Is CoSec App suitable for groups?", a: "Yes, built for in-house Cosec teams, accountancy firms and corporate law practitioners managing up to thousands of entities." },
    { q: "Do you have a free trial?", a: "Yes, every plan offers a free trial with no credit card required." },
  ]},
  { title: "Compliance", items: [
    { q: "Are you a Companies House Authorised Software Provider?", a: "Yes, every filing we do goes via the Companies House software gateway." },
    { q: "Is CoSec App ECCT Act 2023 compliant?", a: "Yes, including identity verification, registered office validation and the new statutory data points." },
    { q: "Do you handle the Companies House 2027 software-only mandate?", a: "Yes, we are already on the gateway, so when April 2027 lands you are on the compliant rails." },
  ]},
  { title: "Security", items: [
    { q: "Where is my data hosted?", a: "Encrypted AWS infrastructure in the UK / EEA. ISO 27001 and ISO 27701 certified." },
    { q: "Do you support SSO and MFA?", a: "Yes. Okta, Azure AD, Google Workspace SSO; TOTP and WebAuthn MFA." },
    { q: "Can I export my data?", a: "Yes, at any time. Full export via the API, plus PDF + CSV per entity." },
  ]},
  { title: "Pricing", items: [
    { q: "How is CoSec App priced?", a: "Per entity, on a transparent monthly or annual plan. See /pricing." },
    { q: "Are there setup fees?", a: "No, all plans include onboarding." },
    { q: "Do you offer charity discounts?", a: "Yes, 20% off any plan for registered UK charities and not-for-profits." },
  ]},
];
export function FaqsPage() {
  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-20 pb-10 max-w-4xl text-center mx-auto">
          <span className="section-eyebrow">FAQs</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">Questions, answered.</h1>
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="container-cv max-w-4xl space-y-10">
          {FAQ_GROUPS.map((g, gi) => (
            <div key={gi}>
              <h2 className="font-display text-2xl font-bold text-primary tracking-tight mb-4">{g.title}</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {g.items.map((f, i) => (
                  <AccordionItem key={i} value={`g${gi}-${i}`} data-testid={`faqs-${gi}-${i + 1}`} className="rounded-xl border border-border bg-white px-5">
                    <AccordionTrigger className="text-left font-display font-bold text-primary hover:no-underline py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}

// ---------- /partners ----------
const PARTNER_LOGOS = ["Xero", "QuickBooks", "Sage", "FreeAgent", "Companies House", "Stripe", "DocuSign", "iManage", "NetDocuments", "HubSpot", "Slack"];
export function PartnersPage() {
  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-24 pb-12 max-w-4xl">
          <span className="pill"><Handshake className="h-3.5 w-3.5 text-accent" /> Partnerships</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            We work with the tools{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">UK firms already use.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">CoSec App connects with 200+ accounting, signing, messaging and matter-management tools, so Cosec data flows where your team already lives.</p>
        </div>
      </section>
      <section className="pb-12">
        <div className="container-cv">
          <div className="rounded-3xl border border-border bg-secondary/40 p-8 md:p-12">
            <div className="text-xs font-bold uppercase tracking-wider text-primary/60 text-center">Integration partners</div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {PARTNER_LOGOS.map((p) => (
                <div key={p} className="font-display text-base md:text-lg font-bold text-primary/70 text-center py-4 border border-border bg-white rounded-xl">{p}</div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="container-cv grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-white p-6">
            <div className="font-display text-xl font-bold text-primary">Become a referral partner</div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Accountancy firms, law firms and management consultancies refer clients to CoSec. 20% commission on first-year revenue.</p>
            <Button asChild className="mt-5 h-11 bg-primary text-primary-foreground"><Link to="/contact">Apply</Link></Button>
          </div>
          <div className="rounded-2xl border border-border bg-white p-6">
            <div className="font-display text-xl font-bold text-primary">Integration partner</div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Build with our REST API. We co-market, you get featured in our directory. Sandbox key on request.</p>
            <Button asChild variant="outline" className="mt-5 h-11 border-primary/20 text-primary"><Link to="/contact">Talk to us</Link></Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

// ---------- /company-secretarial-compliance-uk-guide ----------
export function ComplianceGuidePage() {
  const sections = [
    { t: "1. The UK regulatory landscape", p: "UK Cosec compliance sits across three statutes: the Companies Act 2006, the Money Laundering Regulations 2017, and the Economic Crime and Corporate Transparency Act 2023. Together they govern how UK companies are formed, run, filed with Companies House, and verified for AML purposes." },
    { t: "2. The role of the company secretary", p: "The secretary acts as the bridge between directors and shareholders, maintains statutory registers, runs board governance, and files with Companies House. In a UK PLC the role is mandatory; in private companies it's often combined with finance or in-house legal." },
    { t: "3. Statutory registers, what to keep", p: "Members, directors, secretaries, PSCs, charges, debenture holders. Each register has a prescribed format and minimum data. CoSec App ships templates aligned to the Act." },
    { t: "4. Companies House filings, annual cycle", p: "CS01 confirmation statement, annual accounts, PSC updates, director/secretary changes, share allotments and transfers. The 2027 software-only mandate means WebFiling is going away." },
    { t: "5. ECCT Act 2023, what's new", p: "Identity verification for every director and PSC. Registered office must be capable of receiving mail. Registered email required. Companies House gets investigation powers." },
    { t: "6. Board meetings and resolutions", p: "How to schedule, produce agendas and board packs, capture minutes (AI-assisted is increasingly the norm), e-sign resolutions and archive against the entity." },
    { t: "7. PSC analysis", p: "Identify, verify and notify Companies House of every PSC. Re-analyse on every share event." },
    { t: "8. Year-end & accounts", p: "Annual accounts filing timelines depend on company size. Coordination with the auditor and finance team is the secretary's responsibility." },
    { t: "9. Group governance", p: "Subsidiaries, intra-group resolutions, PSC chains through holding structures, audit-trail consistency." },
    { t: "10. Audit-trail discipline", p: "Every action timestamped and user-attributed. Holds up in due diligence and in litigation." },
    { t: "11. Cybersecurity & data", p: "ISO 27001 hosting, role-based access, encrypted storage, MFA, table stakes." },
    { t: "12. Looking ahead, 2027 and beyond", p: "Companies House software-only filing, the ECCTA reforms continuing to roll out, AI-driven minute-taking becoming standard. Get on the rails now." },
  ];
  return (
    <SiteLayout>
      <section className="container-cv pt-14 md:pt-24 pb-10 max-w-4xl">
        <span className="pill"><BookOpen className="h-3.5 w-3.5 text-accent" /> 12-section deep dive · 22 min read</span>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
          The complete UK company secretarial{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-accent">compliance guide.</span>
            <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
          </span>
        </h1>
        <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">Companies Act 2006, the ECCT Act 2023, the 2027 software-only mandate, and how the company secretary actually navigates all of it.</p>
      </section>
      <section className="container-cv pb-20 md:pb-28 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3">
          <div className="sticky top-28 rounded-xl border border-border bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Sections</div>
            <ol className="mt-3 space-y-2">
              {sections.map((s, i) => <li key={i}><a href={`#s-${i}`} className="text-sm text-primary/80 hover:text-accent">{s.t}</a></li>)}
            </ol>
          </div>
        </aside>
        <article className="lg:col-span-9 max-w-3xl space-y-10">
          {sections.map((s, i) => (
            <section key={i} id={`s-${i}`} className="scroll-mt-28">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">{s.t}</h2>
              <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed">{s.p}</p>
            </section>
          ))}
        </article>
      </section>
    </SiteLayout>
  );
}

// ---------- /business-intelligence-platform ----------
export function BusinessIntelligencePage() {
  return (
    <SiteLayout>
      <section className="container-cv pt-14 md:pt-24 pb-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <span className="pill"><BarChart3 className="h-3.5 w-3.5 text-accent" /> Business intelligence</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            See your entire entity portfolio,{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">at a glance.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">Real-time dashboards across compliance health, filing deadlines, PSC verification status and audit readiness, one screen, the whole group.</p>
          <Button asChild className="mt-7 h-12 px-6 bg-primary text-primary-foreground font-semibold"><Link to="/contact">Book a Demo <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-border bg-white p-5">
            <div className="grid grid-cols-3 gap-3">
              {[{k:"126", l:"Entities"}, {k:"98%", l:"Compliance"}, {k:"12", l:"Due this week"}].map((s, i) => (
                <div key={i} className="rounded-lg bg-secondary/60 p-3 text-center">
                  <div className="font-display text-2xl font-bold text-primary">{s.k}</div>
                  <div className="text-[10px] text-muted-foreground">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-lg bg-secondary/40 p-3">
              <div className="h-2 w-1/2 rounded-full bg-primary/30" />
              <div className="mt-2 h-24 rounded-md bg-gradient-to-r from-primary/10 to-accent/20" />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

// ---------- /coform. UK Company Formation (authorised Companies House agent) ----------
export function CoformPage() {
  const PRODUCT_LD = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Company Formation",
    name: "CoForm. UK company formation",
    description:
      "CoForm is the company-formation service from CoSec Global, an authorised agent of Companies House. AI-guided 4-step online formation with digital statutory documents.",
    provider: {
      "@type": "Organization",
      name: "CoSec Global",
      url: SITE_BASE || "https://cosecglobal.com",
    },
    areaServed: { "@type": "Country", name: "United Kingdom" },
    url: `${SITE_BASE}/coform`,
    offers: {
      "@type": "Offer",
      priceCurrency: "GBP",
      availability: "https://schema.org/OnlineOnly",
      category: "UK company incorporation",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "UK Company Formation Packages",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Digital formation pack" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Secretary-supported formation" } },
      ],
    },
  };
  const HOWTO_LD = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to register a UK company with CoForm",
    description: "Form your UK limited company in 4 steps with CoForm, an authorised Companies House agent.",
    totalTime: "PT1H",
    step: [
      { "@type": "HowToStep", position: 1, name: "Choose a Company Name", text: "Prepare three potential names. We instantly check availability against Companies House." },
      { "@type": "HowToStep", position: 2, name: "Choose a Package", text: "Pick the formation package that suits you, fast-track digital or secretary-supported." },
      { "@type": "HowToStep", position: 3, name: "Checkout", text: "Review your order and pay securely via Stripe. You'll receive an invoice immediately." },
      { "@type": "HowToStep", position: 4, name: "Fill your Details & Submit", text: "Enter officer, shareholder and PSC info. We submit to Companies House and deliver every digital document back to you." },
    ],
  };
  const BREADCRUMB_LD = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_BASE}/` },
      { "@type": "ListItem", position: 2, name: "CoForm", item: `${SITE_BASE}/coform` },
    ],
  };

  return (
    <SiteLayout>
      <StructuredData data={PRODUCT_LD} id="ld-coform-service" />
      <StructuredData data={HOWTO_LD} id="ld-coform-howto" />
      <StructuredData data={BREADCRUMB_LD} id="ld-coform-breadcrumb" />
      <section className="dark-hero">
        <div className="container-cv pt-14 md:pt-24 pb-12 max-w-5xl">
        <span className="pill"><FileSpreadsheet className="h-3.5 w-3.5 text-accent" /> CoForm</span>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
          Register your UK company,{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-accent">ready to trade in hours.</span>
            <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
          </span>
        </h1>
        <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
          CoForm is the company-formation service from CoSec Global, an authorised agent of Companies House.
          Our AI-guided online platform forms your company in four simple steps, with all statutory documents
          delivered digitally. No paperwork, no waiting weeks for incorporation.
        </p>

        {/* Company name availability search */}
        <CompanyNameSearch />

        {/* Trust pills row */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
          {[
            { Icon: Building2, k: "200K+", v: "Companies registered" },
            { Icon: Clock4, k: "< 3h", v: "Processing time" },
            { Icon: Award, k: "ISO 27001", v: "Certified" },
            { Icon: ShieldCheck, k: "GDPR", v: "Compliant" },
          ].map((it, i) => (
            <div key={i} className="rounded-xl border border-white/15 bg-white/[0.05] backdrop-blur-sm p-3 text-center">
              <it.Icon className="h-4 w-4 text-accent mx-auto" />
              <div className="mt-1.5 font-display text-base font-bold text-white leading-tight">{it.k}</div>
              <div className="text-[10px] text-white/70 uppercase tracking-wider mt-0.5">{it.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
            <a href="#packages" data-testid="coform-start-cta">Select your package</a>
          </Button>
          <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-semibold bg-white/[0.04]">
            <Link to="/pricing">View packages</Link>
          </Button>
        </div>
        </div>
      </section>

      {/* Package selection */}
      <PackageSelection />

      {/* 4-step process */}
      <section className="border-b border-border">
        <div className="container-cv py-16 md:py-20">
          <div className="max-w-2xl">
            <span className="section-eyebrow">How it works</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary tracking-tight leading-tight">
              Establish your company in <span className="text-accent">4 steps.</span>
            </h2>
          </div>
          <ol className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                n: "01",
                t: "Choose a Company Name",
                d: "Prepare a list of at least three potential names. We instantly check availability against the Companies House register.",
              },
              {
                n: "02",
                t: "Choose a Package",
                d: "Pick the formation package that suits you, from a fast-track digital pack to a full secretary-supported bundle.",
              },
              {
                n: "03",
                t: "Checkout",
                d: "Review your order and add-ons. Payment is secured through Stripe, you'll receive an invoice immediately.",
              },
              {
                n: "04",
                t: "Fill your Details & Submit",
                d: "Enter officer, shareholder and PSC info. We submit to Companies House and deliver every digital document back to you.",
              },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-border bg-white p-5">
                <div className="font-display text-3xl font-black text-accent">{s.n}</div>
                <div className="mt-2 font-display text-lg font-bold text-primary tracking-tight">{s.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Get more from CoSec Global */}
      <section className="container-cv py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="section-eyebrow">Why CoSec Global</span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary tracking-tight leading-tight">
            Get more from <span className="text-accent">CoSec Global.</span>
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            CoSec Global is a cloud-based platform that gives companies and advisors the means to access and oversee corporate
            information, simplifying complex processes and demystifying unfamiliar tasks across every entity you manage.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { t: "Security", d: "Industry-standard application security and protocols. All data hosted on AWS, the leading provider in the cloud services market." },
            { t: "24/7 Support", d: "Our support team is staffed with dedicated experts who have a deep understanding of our products and services for swift, effective solutions." },
            { t: "200+ Integrations", d: "Connect your apps, simplify your tasks, and supercharge your productivity. CoSec Global unlocks a new level of efficiency." },
          ].map((b) => (
            <div key={b.t} className="rounded-2xl border border-border bg-white p-6">
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary">
                <FileSpreadsheet className="h-5 w-5" />
              </div>
              <div className="mt-4 font-display text-lg font-bold text-primary">{b.t}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-primary text-primary-foreground p-8 md:p-10 relative overflow-hidden">
          <div className="absolute inset-0 bg-dotgrid opacity-[0.06] pointer-events-none" />
          <div className="relative grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                <span className="h-px w-6 bg-accent" /> After formation
              </span>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold leading-tight">
                Streamline company secretarial work with <span className="text-accent">CoSec App.</span>
              </h3>
              <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed">
                Once your company is formed, CoSec App helps you manage compliance, entity governance and document tracking, saving you time, reducing errors and keeping your team in sync.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
                <Link to="/contact">Book a Demo</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
